import * as authHooks from "@rentiohq/shared-frontend/dist/redux/auth/auth.hooks";
import * as brokerHooks from "@rentiohq/shared-frontend/dist/redux/broker/broker.hooks";
import { ITemplateDocument } from "@rentiohq/shared-frontend/dist/redux/templateDocument/templateDocument.types";
import { EBrokerFeature } from "@rentiohq/shared-frontend/dist/types/broker.types";
import { IContract } from "@rentiohq/shared-frontend/dist/types/contract.types";
import { IDocumentPackage } from "@rentiohq/shared-frontend/dist/types/documentPackage.types";
import { IPaymentOrder } from "@rentiohq/shared-frontend/dist/types/payment.types";
import { IProperty } from "@rentiohq/shared-frontend/dist/types/property.types";
import { IRentDeposit } from "@rentiohq/shared-frontend/dist/types/rentDeposit.types";
import { getLocalizedText } from "@rentiohq/shared-frontend/dist/utils/i18n/i18n.utils";
import { IChecklistItem } from "../ChecklistItem";
import {
  EAdministrationItems,
  IAdministrationItem,
} from "./AdministrationItems.types";
import * as util from "./AdministrationItems.utils";

interface IProps {
  contract: IContract;
  property: IProperty;
  rentDeposits?: IRentDeposit[];
  paymentOrdersV1?: IPaymentOrder[];
  paymentOrdersV2?: IPaymentOrder[];
  documentPackages?: IDocumentPackage[];
  shouldFetchData?: boolean;
  checklistItems?: { [k: string]: IChecklistItem | undefined };
  contractTemplateDocuments?: ITemplateDocument[];
}

export const useGetAdministrationEntryItems = ({
  contract,
  property,
  rentDeposits,
  paymentOrdersV1,
  paymentOrdersV2,
  documentPackages,
  checklistItems,
  contractTemplateDocuments,
  shouldFetchData,
}: IProps) => {
  const { isBroker } = authHooks.useSelf();

  const hasRentioMoveForBroker = brokerHooks.useBrokerFeature(
    EBrokerFeature.RentioMove,
  );

  const hasInsuranceForBroker = brokerHooks.useBrokerFeature(
    EBrokerFeature.Insurance,
  );

  const hasPaymentV2ForBroker = brokerHooks.useBrokerFeature(
    EBrokerFeature.PaymentV2,
  );

  if (!checklistItems) {
    return;
  }

  const getItemLabel = (key: EAdministrationItems) =>
    getLocalizedText(`contract.administration_entry_item.${key}.label`);

  const hasFireInsurance = (isBroker && hasInsuranceForBroker) || !isBroker;
  const hasRentioMove = (isBroker && hasRentioMoveForBroker) || !isBroker;

  // @ts-ignore
  const administrationEntryItems: IAdministrationItem[] = [
    {
      id: EAdministrationItems.Contract,
      label: getItemLabel(EAdministrationItems.Contract),
      icon: "contract",
      status: util.getTemplateDocumentContractStatus({
        contract,
        contractTemplateDocuments,
      }),
      tooltip: util.getTemplateDocumentContractTooltip({
        contract,
        contractTemplateDocuments,
      }),
      link: checklistItems[EAdministrationItems.Contract]?.link,
      onClick: checklistItems[EAdministrationItems.Contract]?.onClick,
    },
    {
      id: EAdministrationItems.Sign,
      label: getItemLabel(EAdministrationItems.Sign),
      icon: "contentWrite",
      status: util.getDocumentPackageStatus({ contract, documentPackages }),
      tooltip: util.getDocumentPackageTooltip({ contract, documentPackages }),
      link: checklistItems[EAdministrationItems.Sign]?.link,
      onClick: checklistItems[EAdministrationItems.Sign]?.onClick,
    },
    {
      id: EAdministrationItems.Deposit,
      label: getItemLabel(EAdministrationItems.Deposit),
      icon: "safe",
      status: util.getRentDepositStatus({ contract, rentDeposits }),
      tooltip: util.getRentDepositTooltip({ contract, rentDeposits }),
      link: checklistItems[EAdministrationItems.Deposit]?.link,
      onClick: checklistItems[EAdministrationItems.Deposit]?.onClick,
    },
    {
      id: EAdministrationItems.Rent,
      label: getItemLabel(EAdministrationItems.Rent),
      icon: "currencyEuroCircle",
      status: hasPaymentV2ForBroker
        ? util.getPaymentOrderV2Status({
            contract,
            paymentOrders: paymentOrdersV2,
          })
        : util.getPaymentOrderStatus({
            contract,
            paymentOrders: paymentOrdersV1,
          }),
      tooltip: hasPaymentV2ForBroker
        ? util.getPaymentOrderV2Tooltip({
            contract,
            paymentOrders: paymentOrdersV2,
          })
        : util.getPaymentOrderTooltip({
            contract,
            paymentOrders: paymentOrdersV1,
          }),
      link: checklistItems[EAdministrationItems.Rent]?.link,
      onClick: checklistItems[EAdministrationItems.Rent]?.onClick,
    },
    {
      id: EAdministrationItems.PropertyInspectionReport,
      label: getItemLabel(EAdministrationItems.PropertyInspectionReport),
      icon: "houseNotes",
      status: util.getPropertyInspectionReportStatus({ contract }),
      tooltip: util.getPropertyInspectionReportTooltip({ contract }),
      link: checklistItems[EAdministrationItems.PropertyInspectionReport]?.link,
      onClick:
        checklistItems[EAdministrationItems.PropertyInspectionReport]?.onClick,
    },
    {
      id: EAdministrationItems.FireInsurance,
      label: getItemLabel(EAdministrationItems.FireInsurance),
      icon: "fire",
      status: util.getInsuranceStatus({
        contract,
        hasFeature: hasFireInsurance,
      }),
      tooltip: util.getInsuranceTooltip({
        contract,
        hasFeature: hasFireInsurance,
      }),
      link: checklistItems[EAdministrationItems.FireInsurance]?.link,
      onClick: checklistItems[EAdministrationItems.FireInsurance]?.onClick,
    },
    {
      id: EAdministrationItems.Energy,
      label: getItemLabel(EAdministrationItems.Energy),
      icon: "chargingBatteryCable",
      status: util.getRentioMoveServiceStatus({
        contract,
        hasFeature: hasRentioMove,
      }),
      tooltip: util.getRentioMoveServiceTooltip({
        contract,
        hasFeature: hasRentioMove,
      }),
      link: checklistItems[EAdministrationItems.Energy]?.link,
      onClick: checklistItems[EAdministrationItems.Energy]?.onClick,
    },
    {
      id: EAdministrationItems.Register,
      label: getItemLabel(EAdministrationItems.Register),
      icon: "register",
      status: util.getRegistrationStatus({ contract, property }),
      tooltip: util.getRegistrationTooltip({ contract, property }),
      link: checklistItems[EAdministrationItems.Register]?.link,
      onClick: checklistItems[EAdministrationItems.Register]?.onClick,
    },
  ];

  return administrationEntryItems;
};

export const useGetAdministrationExitItems = ({
  contract,
  rentDeposits,
  paymentOrdersV1,
  paymentOrdersV2,
  checklistItems,
}: IProps) => {
  // const hasRentioMoveForBroker = brokerHooks.useBrokerFeature(
  //   EBrokerFeature.RentioMove,
  // );

  const hasPaymentV2ForBroker = brokerHooks.useBrokerFeature(
    EBrokerFeature.PaymentV2,
  );

  if (!checklistItems) {
    return;
  }

  const getItemLabel = (key: EAdministrationItems) =>
    getLocalizedText(`contract.administration_entry_item.${key}.label`);

  // const hasRentioMove = (isBroker && hasRentioMoveForBroker) || !isBroker;

  // @ts-ignore
  const administrationExitItems: IAdministrationItem[] = [
    {
      id: EAdministrationItems.DepositRelease,
      label: getItemLabel(EAdministrationItems.DepositRelease),
      icon: "safe",
      status: util.getRentDepositReleaseStatus({ contract, rentDeposits }),
      tooltip: util.getRentDepositReleaseTooltip({ contract, rentDeposits }),
      link: checklistItems[EAdministrationItems.DepositRelease]?.link,
      onClick: checklistItems[EAdministrationItems.DepositRelease]?.onClick,
    },
    {
      id: EAdministrationItems.OutstandingPayments,
      label: getItemLabel(EAdministrationItems.OutstandingPayments),
      icon: "currencyEuroCircle",
      status: hasPaymentV2ForBroker
        ? util.getOutstandingPaymentOrderV2Status({
            contract,
            paymentOrders: paymentOrdersV2,
          })
        : util.getOutstandingPaymentOrderStatus({
            contract,
            paymentOrders: paymentOrdersV1,
          }),
      tooltip: hasPaymentV2ForBroker
        ? util.getOutstandingPaymentOrderV2Tooltip({
            contract,
            paymentOrders: paymentOrdersV2,
          })
        : util.getOutstandingPaymentOrderTooltip({
            contract,
            paymentOrders: paymentOrdersV1,
          }),
      link: checklistItems[EAdministrationItems.OutstandingPayments]?.link,
      onClick:
        checklistItems[EAdministrationItems.OutstandingPayments]?.onClick,
    },
    {
      id: EAdministrationItems.OutgoingPropertyInspectionReport,
      label: getItemLabel(
        EAdministrationItems.OutgoingPropertyInspectionReport,
      ),
      icon: "houseNotes",
      status: util.getOutgoingPropertyInspectionReportStatus({ contract }),
      tooltip: util.getOutgoingPropertyInspectionReportTooltip({ contract }),
      link: checklistItems[
        EAdministrationItems.OutgoingPropertyInspectionReport
      ]?.link,
      onClick:
        checklistItems[EAdministrationItems.OutgoingPropertyInspectionReport]
          ?.onClick,
    },
    // {
    //   id: EAdministrationItems.OutgoingMove,
    //   label: getItemLabel(EAdministrationItems.OutgoingMove),
    //   icon: "chargingBatteryCable",
    //   status: util.getRentioMoveServiceStatus({
    //     contract,
    //     hasFeature: hasRentioMove,
    //   }),
    //   tooltip: util.getRentioMoveServiceTooltip({
    //     contract,
    //     hasFeature: hasRentioMove,
    //   }),
    //   link: checklistItems[EAdministrationItems.OutgoingMove]?.link,
    //   onClick: checklistItems[EAdministrationItems.OutgoingMove]?.onClick,
    // },
  ];

  return administrationExitItems;
};
